/**
 * PricingCard - Beautiful pricing card with gradient accents
 */

import React from 'react';
import { Check, Crown, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/Badge';
import type { SubscriptionPlanResponse } from '@neokanban/types';

interface PricingCardProps {
  plan: SubscriptionPlanResponse;
  billingPeriod: 'monthly' | 'yearly';
  isCurrentPlan?: boolean;
  isPopular?: boolean;
  onSelectPlan: (planId: string, billingPeriod: 'monthly' | 'yearly') => void;
  loading?: boolean;
}

export const PricingCard: React.FC<PricingCardProps> = ({
  plan,
  billingPeriod,
  isCurrentPlan = false,
  isPopular = false,
  onSelectPlan,
  loading = false,
}) => {
  const price = billingPeriod === 'monthly' ? plan.priceMonthly : plan.priceYearly / 12;
  const totalYearlyPrice = billingPeriod === 'yearly' ? plan.priceYearly : plan.priceMonthly * 12;
  const features = Array.isArray(plan.features) ? plan.features : Object.values(plan.features);
  const isFree = plan.priceMonthly === 0 && plan.priceYearly === 0;

  const getPlanGradient = () => {
    if (isFree) return 'from-gray-500 to-gray-700';
    if (plan.name.toLowerCase() === 'pro') return 'from-blue-500 to-purple-600';
    if (plan.name.toLowerCase() === 'business') return 'from-purple-500 to-pink-600';
    if (plan.name.toLowerCase() === 'enterprise') return 'from-orange-500 to-red-600';
    return 'from-blue-500 to-cyan-600';
  };

  return (
    <Card
      className={`relative flex flex-col transition-all duration-300 hover:shadow-xl ${
        isPopular
          ? 'border-2 border-primary-500 dark:border-primary-400 shadow-lg scale-105'
          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
      }`}
    >
      {/* Gradient accent bar */}
      <div className={`h-1.5 rounded-t-xl bg-gradient-to-r ${getPlanGradient()}`} />

      {isPopular && (
        <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-10">
          <Badge className="bg-primary-600 text-white border-0 px-4 py-1 shadow-lg">
            <Sparkles className="w-3 h-3 mr-1" />
            Most Popular
          </Badge>
        </div>
      )}

      <CardHeader className="pb-4">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2">
            {!isFree && (
              <div className={`p-1.5 rounded-lg bg-gradient-to-br ${getPlanGradient()}`}>
                <Crown className="w-4 h-4 text-white" />
              </div>
            )}
            <div>
              <CardTitle className="text-2xl font-bold">{plan.name}</CardTitle>
              {isCurrentPlan && (
                <Badge variant="secondary" className="mt-1">Current Plan</Badge>
              )}
            </div>
          </div>
        </div>
        {plan.description && (
          <CardDescription className="text-sm mt-2">{plan.description}</CardDescription>
        )}
      </CardHeader>

      <CardContent className="flex-1 space-y-6">
        {/* Pricing */}
        <div>
          <div className="flex items-baseline gap-1">
            <span className="text-5xl font-bold text-neutral-900 dark:text-neutral-100">
              ${price.toFixed(0)}
            </span>
            <span className="text-lg text-gray-600 dark:text-gray-400">/mo</span>
          </div>
          {billingPeriod === 'yearly' && !isFree && (
            <div className="mt-2 space-y-1">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                ${totalYearlyPrice.toFixed(0)} billed annually
              </p>
              <div className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 dark:bg-green-900/30 rounded-md">
                <span className="text-xs font-semibold text-green-700 dark:text-green-400">
                  Save 17%
                </span>
              </div>
            </div>
          )}
          {plan.trialDays > 0 && !isCurrentPlan && (
            <div className="mt-3 flex items-center gap-1.5 px-3 py-2 bg-blue-50 dark:bg-blue-950/30 rounded-lg border border-blue-200 dark:border-blue-800">
              <Sparkles className="w-4 h-4 text-blue-600 dark:text-blue-400" />
              <span className="text-sm font-medium text-blue-700 dark:text-blue-300">
                {plan.trialDays}-day free trial
              </span>
            </div>
          )}
        </div>

        {/* Features */}
        <div className="space-y-3">
          <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Features included:</h4>
          <ul className="space-y-2.5">
            {features.slice(0, 5).map((feature, index) => (
              <li key={index} className="flex items-start gap-2.5">
                <div className={`mt-0.5 p-0.5 rounded-full bg-gradient-to-br ${getPlanGradient()} flex-shrink-0`}>
                  <Check className="w-3.5 h-3.5 text-white" />
                </div>
                <span className="text-sm text-gray-700 dark:text-gray-300 leading-snug">{feature}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Limits */}
        {plan.limits && (
          <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Resource limits:</h4>
            <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
              {plan.limits.members !== undefined && (
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-gray-400" />
                  <span>
                    {plan.limits.members === -1 ? '∞ Unlimited' : plan.limits.members} team members
                  </span>
                </li>
              )}
              {plan.limits.projects !== undefined && (
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-gray-400" />
                  <span>
                    {plan.limits.projects === -1 ? '∞ Unlimited' : plan.limits.projects} projects
                  </span>
                </li>
              )}
              {plan.limits.storage_mb !== undefined && (
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-gray-400" />
                  <span>
                    {plan.limits.storage_mb === -1 ? '∞ Unlimited' : `${(plan.limits.storage_mb / 1024).toFixed(1)}GB`} storage
                  </span>
                </li>
              )}
            </ul>
          </div>
        )}
      </CardContent>

      <CardFooter className="pt-6">
        <Button
          className={`w-full ${
            isPopular
              ? `bg-gradient-to-r ${getPlanGradient()} hover:opacity-90 text-white border-0`
              : ''
          }`}
          variant={isPopular ? 'default' : 'outline'}
          onClick={() => onSelectPlan(plan.id, billingPeriod)}
          disabled={isCurrentPlan || loading}
          size="lg"
        >
          {isCurrentPlan ? 'Current Plan' : isFree ? 'Get Started' : 'Subscribe Now'}
        </Button>
      </CardFooter>
    </Card>
  );
};
